import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import { useHistory, useLocation } from "react-router-dom";
import useSound from "use-sound";

import api from "../../services/api";
import openSocket from "../../services/socket-io";
import { AuthContext } from "../Auth/AuthContext";
import alertSound from "../../assets/sound.mp3";
import { i18n } from "../../translate/i18n";
import { contactDisplayName } from "../../utils/contactPresentation";
import {
  activeReplyCount,
  applyReplySeen,
  hasActiveReplyForTicket,
  applyFetchedReplyNotifications,
  isReplyActive,
  normalizeReplyNotifications,
  replyPresentation,
  ticketIdFromPath,
  upsertReplyNotification,
} from "../../utils/replyNotifications";

const noop = () => {};
const ReplyNotificationsContext = createContext({
  notifications: [],
  activeCount: 0,
  cards: [],
  subtle: null,
  openNotification: noop,
  markTicketSeen: noop,
  markAllSeen: noop,
  dismissCard: noop,
  dismissSubtle: noop,
});

const MAX_CARDS = 5;
const SOUND_GAP_MS = 1500;
const REFRESH_EVERY_MS = 60 * 1000;

const pageVisible = () => typeof document === "undefined" || document.visibilityState === "visible";
const canNotifyDesktop = () => typeof window !== "undefined" && "Notification" in window;

export const replyContactName = notification =>
  contactDisplayName(notification?.contact || { name: notification?.contactName });

export const ReplyNotificationsProvider = ({ children }) => {
  const { user } = useContext(AuthContext);
  const history = useHistory();
  const location = useLocation();
  const viewingTicketId = ticketIdFromPath(location.pathname);

  const [notifications, setNotifications] = useState([]);
  const [cardTicketIds, setCardTicketIds] = useState([]);
  const [subtleState, setSubtleState] = useState(null);
  const [visible, setVisible] = useState(pageVisible);
  const [clock, setClock] = useState(Date.now);

  const [playNormal] = useSound(alertSound, { volume: 0.9 });
  const [playSoft] = useSound(alertSound, { volume: 0.25 });

  const notificationsRef = useRef(notifications);
  const viewingRef = useRef(viewingTicketId);
  const soundsRef = useRef({ playNormal, playSoft });
  const historyRef = useRef(history);
  const desktopRef = useRef(new Map());
  const waitingDesktopRef = useRef(new Map());
  const cardTicketIdsRef = useRef(cardTicketIds);
  const seenInFlight = useRef(new Set());
  const seenAgain = useRef(new Set());
  const lastSoundAt = useRef(0);
  const fetchState = useRef({ running: false, dirty: false, generation: 0 });

  notificationsRef.current = notifications;
  cardTicketIdsRef.current = cardTicketIds;
  viewingRef.current = viewingTicketId;
  soundsRef.current = { playNormal, playSoft };
  historyRef.current = history;

  const updateNotifications = useCallback(updater => {
    setNotifications(previous => {
      const next = updater(previous);
      notificationsRef.current = next;
      return next;
    });
    fetchState.current.dirty = true;
  }, []);

  const refresh = useCallback(async () => {
    const state = fetchState.current;
    if (state.running) { state.dirty = true; return; }
    const generation = state.generation;
    state.running = true;
    state.dirty = false;
    try {
      const { data } = await api.get("/reply-notifications");
      if (generation !== fetchState.current.generation) return;
      // Um evento chegou enquanto a consulta viajava: esta foto já está velha;
      // o bloco finally busca de novo.
      if (state.dirty) return;
      const next = applyFetchedReplyNotifications(data?.notifications || [], seenInFlight.current);
      notificationsRef.current = next;
      setNotifications(next);
    } catch (_) {
      // Sem rede: o próximo foco, reconexão ou ciclo tenta de novo.
    } finally {
      if (generation === fetchState.current.generation) {
        state.running = false;
        // Um evento chegou durante a consulta: busca de novo para não perder nada.
        if (state.dirty) { state.dirty = false; refresh(); }
      }
    }
  }, []);

  const closeDesktop = useCallback(ticketId => {
    const key = String(ticketId);
    const notification = desktopRef.current.get(key);
    if (!notification) return;
    try { notification.close(); } catch (_) { /* já fechada pelo sistema */ }
    desktopRef.current.delete(key);
  }, []);

  const markTicketSeen = useCallback((ticketId, { force = false } = {}) => {
    if (!ticketId || !user?.id) return;
    const key = String(ticketId);
    if (!force && !hasActiveReplyForTicket(notificationsRef.current, ticketId)) return;
    updateNotifications(previous => applyReplySeen(previous, { ticketOnly: true, ticketId, seenAt: new Date().toISOString() }));
    setCardTicketIds(previous => previous.filter(id => String(id) !== key));
    setSubtleState(previous => (previous && String(previous.ticketId) === key ? null : previous));
    closeDesktop(ticketId);
    // Uma nova resposta pode chegar enquanto o "visto" anterior viaja: envia de
    // novo ao terminar, para ela também ficar vista no servidor.
    if (seenInFlight.current.has(key)) { seenAgain.current.add(key); return; }
    seenInFlight.current.add(key);
    const send = () => api.put("/reply-notifications/tickets/" + ticketId + "/seen")
      .catch(() => refresh())
      .finally(() => {
        if (seenAgain.current.delete(key)) { send(); return; }
        seenInFlight.current.delete(key);
      });
    send();
  }, [user?.id, updateNotifications, closeDesktop, refresh]);

  const markTicketSeenRef = useRef(markTicketSeen);
  markTicketSeenRef.current = markTicketSeen;

  const openNotification = useCallback(notification => {
    if (!notification?.ticketId) return;
    markTicketSeenRef.current(notification.ticketId, { force: isReplyActive(notification) });
    historyRef.current.push("/tickets/" + notification.ticketId);
  }, []);

  const openNotificationRef = useRef(openNotification);
  openNotificationRef.current = openNotification;

  const markAllSeen = useCallback(() => {
    const seenAt = new Date().toISOString();
    const ids = notificationsRef.current.filter(isReplyActive).map(item => item.id);
    if (!ids.length) return;
    updateNotifications(previous => applyReplySeen(previous, { ids, seenAt }));
    setCardTicketIds([]);
    setSubtleState(null);
    desktopRef.current.forEach((_, key) => closeDesktop(key));
    api.put("/reply-notifications/seen-all").catch(() => refresh());
  }, [updateNotifications, closeDesktop, refresh]);

  const dismissCard = useCallback(ticketId => {
    setCardTicketIds(previous => previous.filter(id => String(id) !== String(ticketId)));
  }, []);

  const dismissSubtle = useCallback(() => setSubtleState(null), []);

  const playSound = useCallback(kind => {
    const now = Date.now();
    if (now - lastSoundAt.current < SOUND_GAP_MS) return;
    lastSoundAt.current = now;
    try {
      const play = kind === "soft" ? soundsRef.current.playSoft : soundsRef.current.playNormal;
      if (play) play();
    } catch (_) { /* o navegador pode bloquear áudio antes da primeira interação */ }
  }, []);

  const showDesktop = useCallback(notification => {
    if (!canNotifyDesktop() || window.Notification.permission !== "granted") return;
    try {
      const key = String(notification.ticketId);
      closeDesktop(key);
      const desktop = new window.Notification(replyContactName(notification) + " respondeu", {
        body: notification.lastBody || "Nova mensagem",
        icon: notification.contact?.profilePicUrl || undefined,
        tag: "reply-" + key,
        renotify: true,
      });
      desktop.onclick = event => {
        if (event && event.preventDefault) event.preventDefault();
        try { window.focus(); } catch (_) { /* foco negado pelo navegador */ }
        openNotificationRef.current(notification);
        closeDesktop(key);
      };
      desktopRef.current.set(key, desktop);
    } catch (_) {
      // Alguns navegadores (ex.: Android) só permitem notificação via service worker.
    }
  }, [closeDesktop]);

  const announce = useCallback(notification => {
    const mode = replyPresentation({
      ticketId: notification.ticketId,
      viewingTicketId: viewingRef.current,
      visible: pageVisible(),
    });
    if (mode === "viewing") {
      markTicketSeenRef.current(notification.ticketId, { force: true });
      return;
    }
    if (mode === "subtle") {
      setSubtleState({ ticketId: notification.ticketId, key: notification.id + ":" + (notification.updatedAt || Date.now()) });
      playSound("soft");
      return;
    }
    setCardTicketIds(previous => [notification.ticketId, ...previous.filter(id => String(id) !== String(notification.ticketId))].slice(0, MAX_CARDS));
    playSound("normal");
    if (!pageVisible()) showDesktop(notification);
  }, [playSound, showDesktop]);

  const announceRef = useRef(announce);
  announceRef.current = announce;

  const closeWaitingDesktop = useCallback(ticketId => {
    const key = String(ticketId);
    const notification = waitingDesktopRef.current.get(key);
    if (!notification) return;
    try { notification.close(); } catch (_) { /* já fechada pelo sistema */ }
    waitingDesktopRef.current.delete(key);
  }, []);

  // Cliente na fila de espera (sem responsável): mantém o alerta que o sistema
  // já tinha — som e aviso do sistema —, fora do histórico de respostas.
  const announceWaiting = useCallback(({ message, ticket, contact }) => {
    if (pageVisible() && String(viewingRef.current) === String(ticket.id)) return;
    playSound("normal");
    if (!canNotifyDesktop() || window.Notification.permission !== "granted") return;
    try {
      const key = String(ticket.id);
      closeWaitingDesktop(key);
      const now = new Date();
      const time = String(now.getHours()).padStart(2, "0") + ":" + String(now.getMinutes()).padStart(2, "0");
      const desktop = new window.Notification(i18n.t("tickets.notification.message") + " " + contactDisplayName(contact), {
        body: (message.body || "") + " - " + time,
        icon: contact?.profilePicUrl || undefined,
        tag: key,
        renotify: true,
      });
      desktop.onclick = event => {
        if (event && event.preventDefault) event.preventDefault();
        try { window.focus(); } catch (_) { /* foco negado pelo navegador */ }
        historyRef.current.push("/tickets/" + ticket.id);
        closeWaitingDesktop(key);
      };
      waitingDesktopRef.current.set(key, desktop);
    } catch (_) {
      // Alguns navegadores (ex.: Android) só permitem notificação via service worker.
    }
  }, [playSound, closeWaitingDesktop]);

  const announceWaitingRef = useRef(announceWaiting);
  announceWaitingRef.current = announceWaiting;

  // Conexão em tempo real: o backend envia os avisos só para a sala privada do atendente.
  useEffect(() => {
    fetchState.current = { running: false, dirty: false, generation: fetchState.current.generation + 1 };
    setNotifications([]);
    notificationsRef.current = [];
    setCardTicketIds([]);
    setSubtleState(null);
    desktopRef.current.forEach((_, key) => closeDesktop(key));
    waitingDesktopRef.current.forEach((_, key) => closeWaitingDesktop(key));
    if (!user?.id) return undefined;

    const socket = openSocket();
    socket.on("connect", () => {
      socket.emit("joinNotification");
      refresh();
    });
    socket.on("appMessage", data => {
      if (!data || data.action !== "create" || !data.message || !data.ticket) return;
      const { message, ticket } = data;
      if (message.fromMe || message.read || ticket.userId || ticket.isGroup) return;
      announceWaitingRef.current({ message, ticket, contact: data.contact || ticket.contact });
    });
    socket.on("ticket", data => {
      if (data && (data.action === "updateUnread" || data.action === "delete")) closeWaitingDesktop(data.ticketId);
    });
    socket.on("replyNotification", data => {
      if (!data) return;
      if (data.action === "upsert" && data.notification) {
        const incoming = data.notification;
        if (incoming.userId && String(incoming.userId) !== String(user.id)) return;
        updateNotifications(previous => upsertReplyNotification(previous, incoming));
        if (isReplyActive(incoming)) announceRef.current(incoming);
      } else if (data.action === "seen") {
        updateNotifications(previous => applyReplySeen(previous, data));
      }
    });
    refresh();
    return () => {
      fetchState.current.generation += 1;
      socket.disconnect();
    };
  }, [user?.id, user?.companyId, refresh, updateNotifications, closeDesktop, closeWaitingDesktop]);

  // Aba voltou a ficar visível: atualiza e reavalia o que está na tela.
  useEffect(() => {
    const onVisibility = () => {
      const isVisible = pageVisible();
      setVisible(isVisible);
      if (isVisible && user?.id) refresh();
    };
    document.addEventListener("visibilitychange", onVisibility);
    window.addEventListener("focus", onVisibility);
    return () => {
      document.removeEventListener("visibilitychange", onVisibility);
      window.removeEventListener("focus", onVisibility);
    };
  }, [user?.id, refresh]);

  // Relógio de 1 minuto: tira do histórico o que passou de 24 horas.
  useEffect(() => {
    const timer = setInterval(() => {
      setClock(Date.now());
      if (pageVisible() && user?.id) refresh();
    }, REFRESH_EVERY_MS);
    return () => clearInterval(timer);
  }, [user?.id, refresh]);

  // Pedido de permissão das notificações do sistema: no carregamento e, se o
  // navegador exigir um gesto, no primeiro clique.
  useEffect(() => {
    if (!user?.id || !canNotifyDesktop()) return undefined;
    const ask = () => {
      try {
        if (window.Notification.permission === "default") window.Notification.requestPermission();
      } catch (_) { /* navegador sem suporte */ }
    };
    ask();
    document.addEventListener("click", ask, { once: true });
    return () => document.removeEventListener("click", ask);
  }, [user?.id]);

  // Visualizar a conversa de qualquer forma marca o aviso como visto.
  useEffect(() => {
    if (!visible || !viewingTicketId) return;
    if (hasActiveReplyForTicket(notifications, viewingTicketId)) markTicketSeen(viewingTicketId);
  }, [visible, viewingTicketId, notifications, markTicketSeen]);

  // Entrou (ou voltou) para a tela de um atendimento: os cartões grandes
  // cobririam os botões do atendimento. Eles saem da tela (continuam no sino)
  // e a resposta mais recente vira o aviso discreto.
  useEffect(() => {
    if (!visible || !viewingTicketId) return;
    closeWaitingDesktop(viewingTicketId);
    const current = cardTicketIdsRef.current;
    if (!current.length) return;
    setCardTicketIds([]);
    const next = current.find(id => String(id) !== String(viewingTicketId) && hasActiveReplyForTicket(notificationsRef.current, id));
    if (next) setSubtleState({ ticketId: next, key: "resume:" + next + ":" + Date.now() });
  }, [visible, viewingTicketId, closeWaitingDesktop]);

  // Aviso do sistema fechado quando deixou de estar ativo (visto em outro aparelho).
  useEffect(() => {
    desktopRef.current.forEach((_, key) => {
      if (!hasActiveReplyForTicket(notifications, key)) closeDesktop(key);
    });
  }, [notifications, closeDesktop]);

  useEffect(() => () => {
    desktopRef.current.forEach((_, key) => closeDesktop(key));
    waitingDesktopRef.current.forEach((_, key) => closeWaitingDesktop(key));
  }, [closeDesktop, closeWaitingDesktop]);

  const list = useMemo(() => normalizeReplyNotifications(notifications, clock), [notifications, clock]);

  const cards = useMemo(() => cardTicketIds
    .map(ticketId => list.find(item => isReplyActive(item) && String(item.ticketId) === String(ticketId)))
    .filter(Boolean), [cardTicketIds, list]);

  const subtle = useMemo(() => {
    if (!subtleState) return null;
    const notification = list.find(item => isReplyActive(item) && String(item.ticketId) === String(subtleState.ticketId));
    return notification ? { key: subtleState.key, notification } : null;
  }, [subtleState, list]);

  const value = useMemo(() => ({
    notifications: list,
    activeCount: activeReplyCount(list),
    cards,
    subtle,
    openNotification,
    markTicketSeen,
    markAllSeen,
    dismissCard,
    dismissSubtle,
  }), [list, cards, subtle, openNotification, markTicketSeen, markAllSeen, dismissCard, dismissSubtle]);

  return <ReplyNotificationsContext.Provider value={value}>{children}</ReplyNotificationsContext.Provider>;
};

export const useReplyNotifications = () => useContext(ReplyNotificationsContext);
